/**
 * Ticket Lifecycle v2 — Remaining Approved Work helper (Slice 5A.3a)
 *
 * Pure helper that estimates the minutes of *approved-and-not-yet-completed*
 * technician work on a ticket at a moment in time. This is the missing
 * input that `calculateAdvisorDelayImpact` needs to compute confident
 * overlap-adjusted delay for `diag_plus_work` tickets:
 *
 *   advisorDelayImpactMinutes = max(0, advisorObserved - remainingApprovedWork)
 *
 * Scope: **analytics math only**. NOT scheduling. NOT lifecycle
 * prediction. NOT advisor delay impact UI. Does not feed Chronomancer
 * or affect `tickets.opti_time` / `tickets.opti_finish`.
 *
 * Data-model reality (as of slice 5A.3a):
 *   - **No first-class job-level approval flag exists.** Approval is at
 *     the ticket level via `ticket_business_state` (`awaiting_approval`
 *     → `approved` → `in_progress` → …). The caller is responsible for
 *     gating: only call this helper on tickets at-or-past `approved`.
 *   - **`ticket_jobs.job_status`** is the live per-job status signal.
 *     Observed values: `'pending'`, `'in_progress'`, `'completed'`.
 *   - **Minutes resolution** mirrors `recomputeTicketPredictedMinutes.ts`:
 *       effective_minutes = minutes_override
 *                         ?? predicted_minutes
 *                         ?? standard_minutes  (job catalog, when joined)
 *   - **No per-status timestamps**: there is no historical replay
 *     available for `asOf` today. The input shape accepts `asOf` for
 *     forward compatibility but the helper currently does not use it.
 *
 * Computation:
 *   1. Empty `jobs` → `remainingApprovedWorkMinutes = 0`, method
 *      `'job_status_minutes'`. (No jobs ⇒ no remaining work.)
 *   2. For each job, resolve effective minutes via the override → predicted
 *      → standard chain. If any job has no minute signal, the helper
 *      returns `null` with method `'insufficient_data'` — conservative
 *      refusal beats overstating delay.
 *   3. Classify by status:
 *        - `'pending'` or `'in_progress'` → contributes its effective
 *          minutes to `remainingApprovedWorkMinutes`. (We treat
 *          `in_progress` as fully remaining: `actual_minutes` is only
 *          populated on completion in this codebase — see
 *          `JobTimerControls.tsx` — so there is no defensible partial
 *          credit to apply.)
 *        - `'completed'` → contributes `min(actual_minutes,
 *          effective_minutes) ?? effective_minutes` to
 *          `completedApprovedWorkMinutes` (informational); contributes
 *          0 to `remainingApprovedWorkMinutes`.
 *        - Any other string (`null`, `''`, `'cancelled'`, `'declined'`,
 *          `'approved'`, etc.) → method `'insufficient_data'`, return
 *          `null`. We do not invent a "default approved" rule when the
 *          shop has set a non-canonical status value.
 *   4. If any job needed `standard_minutes` fallback → method
 *      `'standard_minutes_fallback'` (a soft signal that the ticket-side
 *      `predicted_minutes` mirror wasn't fully populated; the math still
 *      lands but is one rung less reliable than the override / predicted
 *      path).
 *   5. Result is clamped non-negative.
 *
 * Conservative null vs zero rule:
 *   - **`null`** means "unknown — cannot compute confidently."
 *   - **`0`** means "no remaining approved work" (a real, computed value).
 *   Callers (e.g. `calculateAdvisorDelayImpact`) must NOT coerce `null`
 *   to `0` — doing so would silently overstate advisor delay impact on
 *   tickets the helper refused to commit on.
 *
 * Purity contract:
 *   - Same inputs always produce the same output.
 *   - No `Date.now()`, no `new Date()`, no I/O.
 *   - Does not mutate the input.
 *
 * Boundary: this module is analytics-only. It must not be imported into
 * Chronomancer / scheduling / timer / parking / rollover / bay /
 * invariants hot paths. The drift detector boundary guard pins this
 * (`tests/unit/server/custodyEnumDriftDetector.test.ts` §9).
 */

export type RemainingApprovedWorkMethod =
  | 'job_status_minutes'
  | 'standard_minutes_fallback'
  | 'insufficient_data';

/**
 * Minimal per-job shape. Accepts both DB snake_case (`job_status`,
 * `predicted_minutes`, `minutes_override`, `actual_minutes`,
 * `standard_minutes`) and the camelCase variants Ticket-side code may
 * use. All fields optional — but a job needs at least one usable
 * minute signal (override → predicted → standard) to count.
 */
export interface RemainingApprovedWorkJobLike {
  job_status?: string | null;
  jobStatus?: string | null;
  predicted_minutes?: number | null;
  predictedMinutes?: number | null;
  minutes_override?: number | null;
  minutesOverride?: number | null;
  standard_minutes?: number | null;
  standardMinutes?: number | null;
  actual_minutes?: number | null;
  actualMinutes?: number | null;
}

export interface RemainingApprovedWorkInput {
  jobs: ReadonlyArray<RemainingApprovedWorkJobLike>;
  /**
   * Reserved for future per-status replay. Accepted but currently
   * unused — no per-job approval/status timestamps exist in the data
   * model today. Will be honoured once such a signal lands.
   */
  asOf?: string | null;
}

export interface RemainingApprovedWorkResult {
  /** `null` when the helper cannot compute confidently (preferred over `0`). */
  remainingApprovedWorkMinutes: number | null;
  method: RemainingApprovedWorkMethod;
  totalApprovedWorkMinutes: number;
  completedApprovedWorkMinutes: number;
  remainingJobCount: number;
  notes: string[];
}

const COMPLETED_STATUS = 'completed';
const REMAINING_STATUSES = new Set(['pending', 'in_progress']);

function pickNumber(...values: Array<number | null | undefined>): number | null {
  for (const v of values) {
    if (v !== null && v !== undefined && Number.isFinite(v)) return v as number;
  }
  return null;
}

function pickString(
  ...values: Array<string | null | undefined>
): string | null {
  for (const v of values) {
    if (v !== null && v !== undefined && v !== '') return v;
  }
  return null;
}

function clampNonNegative(value: number): number {
  if (!Number.isFinite(value) || value < 0) return 0;
  return value;
}

export function computeRemainingApprovedWorkMinutes(
  input: RemainingApprovedWorkInput,
): RemainingApprovedWorkResult {
  const { jobs } = input;

  if (jobs.length === 0) {
    return {
      remainingApprovedWorkMinutes: 0,
      method: 'job_status_minutes',
      totalApprovedWorkMinutes: 0,
      completedApprovedWorkMinutes: 0,
      remainingJobCount: 0,
      notes: ['No jobs present; remaining approved work is 0.'],
    };
  }

  let total = 0;
  let completed = 0;
  let remaining = 0;
  let remainingJobCount = 0;
  let usedStandardFallback = false;

  for (const job of jobs) {
    const status = pickString(job.job_status, job.jobStatus);
    const override = pickNumber(job.minutes_override, job.minutesOverride);
    const predicted = pickNumber(job.predicted_minutes, job.predictedMinutes);
    const standard = pickNumber(job.standard_minutes, job.standardMinutes);
    const actual = pickNumber(job.actual_minutes, job.actualMinutes);

    let effective: number | null = override;
    if (effective === null) effective = predicted;
    if (effective === null) {
      effective = standard;
      if (effective !== null) usedStandardFallback = true;
    }

    const normalisedStatus = status?.toLowerCase() ?? null;
    const isCompleted = normalisedStatus === COMPLETED_STATUS;
    const isRemaining =
      normalisedStatus !== null && REMAINING_STATUSES.has(normalisedStatus);

    // Unknown status (non-canonical / null / empty) ⇒ refuse rather than guess.
    if (!isCompleted && !isRemaining) {
      return {
        remainingApprovedWorkMinutes: null,
        method: 'insufficient_data',
        totalApprovedWorkMinutes: 0,
        completedApprovedWorkMinutes: 0,
        remainingJobCount: 0,
        notes: [
          status
            ? `Job has non-canonical status "${status}"; remaining approved work cannot be computed confidently.`
            : 'Job has no job_status set; remaining approved work cannot be computed confidently.',
        ],
      };
    }

    // No usable minute signal on a job that would otherwise count ⇒ refuse.
    if (effective === null) {
      return {
        remainingApprovedWorkMinutes: null,
        method: 'insufficient_data',
        totalApprovedWorkMinutes: 0,
        completedApprovedWorkMinutes: 0,
        remainingJobCount: 0,
        notes: [
          'Job is missing minutes_override, predicted_minutes, and standard_minutes; remaining approved work cannot be computed confidently.',
        ],
      };
    }

    const effectiveSafe = clampNonNegative(effective);
    total += effectiveSafe;

    if (isCompleted) {
      // Informational only — actual time spent on completed jobs.
      // Cap at the job's effective contribution so a runaway
      // actual_minutes can't exceed the job's allotted share.
      const consumed =
        actual !== null ? clampNonNegative(actual) : effectiveSafe;
      completed += Math.min(consumed, effectiveSafe);
    } else {
      // Pending / in_progress jobs are treated as fully remaining.
      // (See note in module doc-comment about partial-credit absence.)
      remaining += effectiveSafe;
      remainingJobCount += 1;
    }
  }

  const remainingClamped = clampNonNegative(remaining);

  const method: RemainingApprovedWorkMethod = usedStandardFallback
    ? 'standard_minutes_fallback'
    : 'job_status_minutes';

  const notes: string[] = [
    'Caller is responsible for ensuring the parent ticket is at or past business state `approved` before treating this value as "approved" remaining work.',
  ];
  if (usedStandardFallback) {
    notes.push(
      'One or more jobs fell back to jobs.standard_minutes because minutes_override and predicted_minutes were unset.',
    );
  }

  return {
    remainingApprovedWorkMinutes: remainingClamped,
    method,
    totalApprovedWorkMinutes: total,
    completedApprovedWorkMinutes: completed,
    remainingJobCount,
    notes,
  };
}
